import { defineStore } from 'pinia';
import { api } from '@/utils/api';
import { useNotificationStore } from '@/store/NotificationStore';
import { usePaymentStore } from '@/store/PaymentStore';
import { useCompanyStore } from '@/store/CompanyStore';
import { useAuthStore } from '@/store/AuthStore';

export const useUserStore = defineStore('user', {
    state: () => ({
        myUserInfo: null,
        searchedUsers: [],
        users: null,
        error: null,
        isVerifyEmailVisible: false,
        invitations: [],
    }),
    actions: {

        async fetchInvitations() {
            try {
                const response = await api.get('/users/invitation');
                console.log(response.data);
                this.invitations = response.data;
            } catch (error) {
                console.error('Error fetching invitations:', error);
            }
        },

        async respondToInvitation(memberId, accept) {
            try {
                await api.put(`/users/invitation/${memberId}`, null, { params: { accept } });
                this.invitations = this.invitations.filter(inv => inv.id !== memberId);
                if (accept) {
                    await useAuthStore().refreshToken();
                    await this.fetchMyUserInfo();
                }
                await this.fetchInvitations();
            } catch (error) {
                console.error('Error responding to invitation:', error);
                throw error;
            }
        },

        async fetchMyUserInfo() {
            try {
                const response = await api.get(`/users/me`);
                await useCompanyStore().fetchCompanies();
                
                console.log(response.data);
                this.myUserInfo = response.data;
                if (!this.myUserInfo.tariff.confirmed) {
                    useNotificationStore().addBanner({
                        id: 'unconfirmed-payment',
                        type: 'error',
                        message: 'Ваша оплата подписки не прошла проверку. Пожалуйста, подтвердите оплату, чтобы продолжить использование сервиса.',
                        button: {
                            text: 'Оплатить',
                            action: () => {
                                usePaymentStore().newPayment();
                            }
                        }
                    });
                }
                if (!this.myUserInfo.verifyEmail) {
                    useNotificationStore().addBanner({
                        id: 'unconfirmed-email',
                        type: 'warning',
                        message: 'Пожалуйста, подтвердите ваш email, чтобы продолжить использование сервиса.',
                        button: {
                            text: 'Подтвердить',
                            action: () => {
                                this.isVerifyEmailVisible = true;
                                // this.sendVerificationCode(this.myUserInfo.email);
                            }
                        }
                    });
                }

                for (const storage of this.myUserInfo.companiesStorage ?? []) {
                    if (storage.usagePercent >= 100) {
                        useNotificationStore().addBanner({
                            id: `storage-full-${storage.companyId}`,
                            type: 'error',
                            message: `Хранилище компании «${storage.companyName}» заполнено. Загрузка новых файлов недоступна.`,
                        });
                    } else if (storage.usagePercent >= 80) {
                        useNotificationStore().addBanner({
                            id: `storage-warning-${storage.companyId}`,
                            type: 'warning',
                            message: `Хранилище компании «${storage.companyName}» заполнено на ${storage.usagePercent}%. Освободите место или обновите тариф.`,
                        });
                    }
                }

                return response.data
            } catch (e) {
                console.error(e);
            }
        },

        async searchUsers(query) {
            try {
                const response = await api.get(`/users?query=${query}`);
                console.log(response.data);
                this.searchedUsers = response.data.content;
            } catch (e) {
                console.error(e);
            }
        },

        async fetchUsersList(page = 0, size = 10, query = '') {
            try {
                const response = await api.get(`/admin/users`, {
                    params: {
                        page,
                        size,
                        query
                    }
                });
                console.log(response.data);
                this.users = response.data;
            } catch (e) {
                console.error(e);
            }
        },

        async sendVerificationCode(email) {
            console.log(email);
            try {
                const response = await api.post(`/users/email/code/send?email=${email ? email : ""}`);
                console.log(response.data);
            } catch (e) {
                this.error = e.response.data.message;
            }
        },

        async submitVerificationCode(code) {
            try {
                const response = await api.post(`/users/email/code?code=${code}`);
                console.log(response.data);
            } catch (e) {
                this.error = e.response.data.message;
            }
        },

        async updatePrivateProfile(privateProfile) {
            try {
                await api.patch(`/users/me/private`, null, { params: { privateProfile } });
                if (this.myUserInfo) {
                    this.myUserInfo.privateProfile = privateProfile;
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        async changePassword(newPassword) {
            try {
                await api.put('/users/password', {
                    newPassword
                });
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        async toggleLlmAccess(userId, access) {
            try {
                const response = await api.put(`/agent/users/${userId}/access`, null, {
                    params: { access }
                });
                // Update local state if needed, or re-fetch list
                if (this.users && this.users.content) {
                    const user = this.users.content.find(u => u.id === userId);
                    if (user) {
                        user.llmEnable = access;
                    }
                }
                return response.data;
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        async unblockUser(userId) {
            try {
                const response = await api.post(`/admin/users/${userId}/unblock`);
                console.log(response.data);
            } catch (e) {
                console.error(e);
                throw e;
            }
        },

        async blockUser(userId) {
            try {
                const response = await api.post(`/admin/users/${userId}/block`);
                console.log(response.data);
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
    }
});
